import { useState, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ToastContext } from "@/hooks/useToast";
import { AppSidebar } from "@/components/AppSidebar";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { Header } from "@/components/Header";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { DashboardPage } from "@/pages/DashboardPage";
import { CalendarPage } from "@/pages/CalendarPage";
import { ReservationCreatePage } from "@/pages/ReservationCreatePage";
import { MyReservationsPage } from "@/pages/MyReservationsPage";
import { ReservationDetailPage } from "@/pages/ReservationDetailPage";
import { PcManagementPage } from "@/pages/PcManagementPage";
import { UserManagementPage } from "@/pages/UserManagementPage";
import { ApprovalManagementPage } from "@/pages/ApprovalManagementPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { LoginPage } from "@/pages/LoginPage";
import type { CurrentUser } from "@/types/user";

function Forbidden() {
  return <EmptyState message="この画面を表示する権限がありません。" />;
}

function RoleGate({
  user,
  allow,
  children,
}: {
  user: CurrentUser;
  allow: CurrentUser["role"][];
  children: ReactNode;
}) {
  if (!allow.includes(user.role)) return <Forbidden />;
  return <>{children}</>;
}

export default function App() {
  const { user, loading, error, unauthenticated, reload } = useCurrentUser();
  const [toast, setToast] = useState<string | null>(null);

  const notify = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast((current) => (current === message ? null : current)), 3000);
  };

  if (loading) {
    return (
      <div className="app-shell">
        <div className="page-content">
          <LoadingState label="ログイン情報を確認中..." />
        </div>
      </div>
    );
  }

  if (unauthenticated) {
    return <LoginPage onLoggedIn={reload} />;
  }

  if (error || !user) {
    return (
      <div className="app-shell">
        <div className="page-content">
          <EmptyState
            message={error ?? "ログインに失敗しました。"}
            action={
              <button className="btn btn-primary" onClick={reload}>
                再試行
              </button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <ToastContext.Provider value={notify}>
      <div className="app-shell">
        <AppSidebar user={user} />
        <div className="app-main">
          <Header user={user} />
          <div className="page-content">
            <Routes>
              <Route path="/" element={<DashboardPage currentUser={user} />} />
              <Route path="/calendar" element={<CalendarPage currentUser={user} />} />
              <Route path="/reservations/new" element={<ReservationCreatePage currentUser={user} />} />
              <Route path="/reservations/:id" element={<ReservationDetailPage currentUser={user} />} />
              <Route path="/my-reservations" element={<MyReservationsPage currentUser={user} />} />
              <Route
                path="/approvals"
                element={
                  <RoleGate user={user} allow={["manager", "admin"]}>
                    <ApprovalManagementPage currentUser={user} />
                  </RoleGate>
                }
              />
              <Route
                path="/pcs"
                element={
                  <RoleGate user={user} allow={["admin"]}>
                    <PcManagementPage />
                  </RoleGate>
                }
              />
              <Route
                path="/users"
                element={
                  <RoleGate user={user} allow={["admin"]}>
                    <UserManagementPage />
                  </RoleGate>
                }
              />
              <Route path="/settings" element={<SettingsPage currentUser={user} onUserChanged={reload} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
        <MobileBottomNav />
      </div>
      {toast && <div className="toast">{toast}</div>}
    </ToastContext.Provider>
  );
}
